import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { 
Box, 
TextField, 
FormControl, 
InputLabel, 
Select, 
MenuItem, 
FormControlLabel, 
Checkbox, 
Radio, 
RadioGroup, 
Typography, 
Button, 
CircularProgress,
Card,
CardContent,
IconButton,
MobileStepper,
Paper,
Alert,
Chip,
LinearProgress,
Accordion,
AccordionSummary,
AccordionDetails
} from '@mui/material';
import { Circle } from '@mui/icons-material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import CloseIcon from '@mui/icons-material/Close';
import { KeyboardArrowUp, KeyboardArrowDown, AttachFile, Delete, CheckCircleOutline, ExpandMore } from '@mui/icons-material';
import { APIAppGraduateCreate, APIApplicationFormListById, APIAppShortCourseCreate, APIAppUnderGraduateCreate } from '../../utilities/APIs';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../users/AuthContext';
import { useNavigate } from 'react-router-dom';
import SkipLogicEngine from '../Common/SkipLogicEngine';
import { ImageCompressor } from '../../utilities/imageCompression';
import ProgrammeSelector from '../../components/ProgrammeSelector';
import { ProgrammeAPI } from '../../utilities/ProgrammeAPI';
import useCustomToast from '../../hooks/useToast';

// Programme Field Component - renders programme selection directly in the form
function ProgrammeField({ field, value = [], onChange, error = null }) {
    const [programmes, setProgrammes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [apiError, setApiError] = useState(null);

    // Load programmes when component mounts
    useEffect(() => {
        loadProgrammes();
    }, [field.programmeLevel, field.facultyFilter]);    const loadProgrammes = async () => {
        try {
            setLoading(true);
            setApiError(null);
            
            const response = await ProgrammeAPI.getActiveStructuredProgrammes();
            console.log('ProgrammeField - Raw API response:', response.data);
            
            // Flatten the structured response into a single list
            let allProgrammes = [];
            if (response.data) {
                for (const facultyKey in response.data) {
                    const facultyObj = response.data[facultyKey];
                    for (const departmentKey in facultyObj) {
                        const departmentObj = facultyObj[departmentKey];
                        for (const levelKey in departmentObj) {
                            const levelArray = departmentObj[levelKey];
                            if (Array.isArray(levelArray)) {
                                allProgrammes = allProgrammes.concat(levelArray);
                            }
                        }
                    }
                }
            }
            
            console.log('ProgrammeField - All flattened programmes:', allProgrammes);
            
            // Filter programmes based on field configuration
            let filteredProgrammes = allProgrammes;
            
            if (field.programmeLevel && field.programmeLevel !== 'all') {
                filteredProgrammes = filteredProgrammes.filter(p => 
                    p.level && p.level.toLowerCase() === field.programmeLevel.toLowerCase()
                );
            }
            
            if (field.facultyFilter && field.facultyFilter !== 'all') {
                filteredProgrammes = filteredProgrammes.filter(p => 
                    p.faculty && p.faculty.toLowerCase().includes(field.facultyFilter.toLowerCase())
                );
            }
            
            console.log('ProgrammeField - Filtered programmes:', filteredProgrammes);
            setProgrammes(filteredProgrammes);
            
        } catch (err) {
            console.error('ProgrammeField - Error loading programmes:', err);
            setApiError(err.message || 'Failed to load programmes');
        } finally {
            setLoading(false);
        }
    };    const handleProgrammeSelect = (programme) => {
        console.log('ProgrammeField - handleProgrammeSelect called');
        console.log('ProgrammeField - Programme:', programme);
        console.log('ProgrammeField - Current value:', value);
        console.log('ProgrammeField - Field maxSelections:', field.maxSelections);
        
        const maxSelections = field.maxSelections || 1;
        const currentSelection = Array.isArray(value) ? value : [];
        
        console.log('ProgrammeField - Current selection array:', currentSelection);
        console.log('ProgrammeField - Max selections:', maxSelections);
        
        if (maxSelections === 1) {
            // Single selection mode - replace current selection with programme ID
            const isCurrentlySelected = currentSelection.includes(programme.id);
            console.log('ProgrammeField - Is currently selected:', isCurrentlySelected);
            const newValue = isCurrentlySelected ? [] : [programme.id];
            console.log('ProgrammeField - New value to set:', newValue);
            onChange(newValue);
        } else {
            // Multiple selection mode
            const isSelected = currentSelection.includes(programme.id);
            console.log('ProgrammeField - Is selected (multi):', isSelected);
            
            if (isSelected) {
                // Remove from selection
                const newValue = currentSelection.filter(id => id !== programme.id);
                console.log('ProgrammeField - Removing, new value:', newValue);
                onChange(newValue);
            } else {
                // Add to selection if under limit
                if (currentSelection.length < maxSelections) {
                    const newValue = [...currentSelection, programme.id];
                    console.log('ProgrammeField - Adding, new value:', newValue);
                    onChange(newValue);
                }
            }
        }
    };    const isSelected = (programme) => {
        const currentSelection = Array.isArray(value) ? value : [];
        const result = currentSelection.includes(programme.id);
        console.log(`ProgrammeField - isSelected check for ${programme.name} (ID: ${programme.id}):`, result);
        console.log(`ProgrammeField - Current selection:`, currentSelection);
        return result;
    };

    const groupProgrammesByFaculty = (programmes) => {
        const grouped = {};
        programmes.forEach(programme => {
            const faculty = programme.faculty || 'Unknown Faculty';
            if (!grouped[faculty]) {
                grouped[faculty] = {};
            }
            
            const department = programme.department || 'Unknown Department';
            if (!grouped[faculty][department]) {
                grouped[faculty][department] = [];
            }
            
            grouped[faculty][department].push(programme);
        });
        return grouped;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading programmes...</Typography>
            </Box>
        );
    }

    if (apiError) {
        return (
            <Alert severity="error" sx={{ my: 2 }}>
                {apiError}
            </Alert>
        );
    }

    if (!programmes || programmes.length === 0) {
        return (
            <Alert severity="info" sx={{ my: 2 }}>
                No programmes available for the selected criteria.
            </Alert>
        );
    }

    const groupedProgrammes = groupProgrammesByFaculty(programmes);
    const maxSelections = field.maxSelections || 1;

    return (
        <Box sx={{ my: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
                {field.label}
                {field.required && <span style={{ color: 'red' }}> *</span>}
            </Typography>

            {/* Selection info */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {maxSelections === 1 ? 'Select one programme' : `Select up to ${maxSelections} programmes`}
                {value.length > 0 && ` (${value.length} selected)`}
            </Typography>            {/* Selected programmes display */}
            {value.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                        Selected:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {value.map((programmeId) => {
                            const programme = programmes.find(p => p.id === programmeId);
                            return programme ? (
                                <Chip
                                    key={programme.id}
                                    label={`${programme.name} (${programme.faculty})`}
                                    onDelete={() => handleProgrammeSelect(programme)}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                />
                            ) : null;
                        })}
                    </Box>
                </Box>
            )}

            {/* Programme selection list */}
            <Box>
                {Object.entries(groupedProgrammes).map(([faculty, departments]) => (
                    <Accordion key={faculty} defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="h6">{faculty}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {Object.entries(departments).map(([department, departmentProgrammes]) => (
                                <Box key={`${faculty}-${department}`} sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium', color: 'text.secondary' }}>
                                        {department}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {departmentProgrammes.map((programme) => (
                                            <Button
                                                key={programme.id}
                                                variant="outlined"
                                                onClick={() => handleProgrammeSelect(programme)}
                                                sx={{
                                                    textTransform: 'none',
                                                    justifyContent: 'flex-start',
                                                    borderColor: isSelected(programme) ? 'primary.main' : 'grey.300',
                                                    backgroundColor: isSelected(programme) ? 'primary.50' : 'transparent',
                                                    '&:hover': {
                                                        backgroundColor: isSelected(programme) ? 'primary.100' : 'grey.50',
                                                    },
                                                    pl: 3,
                                                    py: 1.5
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1.5,
                                                        width: '100%'
                                                    }}
                                                >
                                                    {/* Selection indicator */}
                                                    <Box
                                                        sx={{
                                                            width: maxSelections === 1 ? 16 : 16,
                                                            height: maxSelections === 1 ? 16 : 16,
                                                            border: '2px solid',
                                                            borderColor: isSelected(programme) ? 'primary.main' : 'grey.400',
                                                            borderRadius: maxSelections === 1 ? '50%' : 1,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0,
                                                            bgcolor: isSelected(programme) ? 'primary.main' : 'transparent'
                                                        }}
                                                    >
                                                        {isSelected(programme) && (
                                                            maxSelections === 1 ? (
                                                                <Box
                                                                    sx={{
                                                                        width: 8,
                                                                        height: 8,
                                                                        borderRadius: '50%',
                                                                        bgcolor: 'white',
                                                                    }}
                                                                />
                                                            ) : (
                                                                <CheckCircleOutline sx={{ color: 'white', fontSize: 12 }} />
                                                            )
                                                        )}
                                                    </Box>

                                                    {/* Programme details */}
                                                    <Box sx={{ textAlign: 'left', flex: 1 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                            {programme.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Level: {programme.level}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Button>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>

            {/* Validation error */}
            {error && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
}

export function ApplicationForm({formId, onCancel, demoData = null}) {
    const { makeAuthenticatedRequest, user } = useAuth();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);    const [activeSectionIndex, setActiveSectionIndex] = useState(0);
    const [formValues, setFormValues] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const [fileErrors, setFileErrors] = useState({});
    const [validationErrors, setValidationErrors] = useState({});
    const [sectionValidation, setSectionValidation] = useState({});
    const showToast = useCustomToast();
    
    const fetchForms = async (formId) => {
        setLoading(true);
        setError(null);
        try {            // Use demo data if provided
            if (demoData) {
                const formConfig = typeof demoData.formConfigJson === 'string' 
                    ? JSON.parse(demoData.formConfigJson) 
                    : demoData.formConfigJson;
                
                // Validate that formConfig has sections
                if (!formConfig || !formConfig.sections || !Array.isArray(formConfig.sections)) {
                    showToast('Error!','Form configuration is invalid or missing. The form may not be properly configured','error');
                    // setError('Form configuration is invalid or missing. The form may not be properly configured.');
                    setLoading(false);
                    return;
                }
                
                setFormData({
                    ...demoData,
                    ...formConfig
                });
                // Initialize form values
                const initialValues = {};
                formConfig.sections.forEach(section => {
                    if (section.fields && Array.isArray(section.fields)) {
                        section.fields.forEach(field => {
                            if (field.type === 'programme-selector') {
                                initialValues[field.id] = [];
                            } else if (field.type === 'checkbox') {
                                // Always initialize as boolean false
                                initialValues[field.id] = false;
                                // Or if you want to support default values:
                                // initialValues[field.id] = field.defaultValue || false;
                            } else {
                                initialValues[field.id] = '';
                            }
                        });
                    }
                });
                setFormValues(initialValues);
                setLoading(false);
                return;
            }

            const result = await makeAuthenticatedRequest(APIApplicationFormListById + formId);
            const data = result.data;
            console.log("Form data:", data);
              // Parse the formConfigJson if it's a string
            const formConfig = typeof data.formConfigJson === 'string' 
                ? JSON.parse(data.formConfigJson) 
                : data.formConfigJson;
            
            // Validate that formConfig has sections
            if (!formConfig || !formConfig.sections || !Array.isArray(formConfig.sections)) {
                showToast('Error!','Form configuration is invalid or missing','error');
                throw new Error('Form configuration is invalid or missing');
            }
            setFormData({
                ...data,
                ...formConfig
            });
              // Initialize form values
            const initialValues = {};
            formConfig.sections.forEach(section => {
                if (section.fields && Array.isArray(section.fields)) {
                    section.fields.forEach(field => {
                        if (field.type === 'programme-selector') {
                            initialValues[field.id] = [];
                        } else {
                            initialValues[field.id] = field.type === 'checkbox' ? false : '';
                        }
                    });
                }
            });
            setFormValues(initialValues);
            
        } catch (err) {
            console.log("Error fetching form:", err);
            showToast('Error',err.message || 'Failed to fetch forms','error');
            // setError(err.message || 'Failed to fetch forms');
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        fetchForms(formId);
    }, []);    // Initialize skip logic engine with throttled updates
    const skipLogicEngine = useMemo(() => {
        if (!formData) return null;
        return new SkipLogicEngine(formData, formValues);
    }, [formData, formValues]);

    // Auto-navigate to the first available section when skip logic changes
    useEffect(() => {
        if (skipLogicEngine && formData?.sections?.length > 0) {
            // Check if current section is still available
            if (!skipLogicEngine.canNavigateToSection(formData.sections[activeSectionIndex]?.id)) {
                // Find the first available section
                for (let i = 0; i < formData.sections.length; i++) {
                    if (skipLogicEngine.canNavigateToSection(formData.sections[i].id)) {
                        setActiveSectionIndex(i);
                        break;
                    }
                }
            }
        }
    }, [skipLogicEngine, formData, activeSectionIndex]);

    // Get visible sections based on skip logic
    const visibleSections = useMemo(() => {
        if (!skipLogicEngine) return formData?.sections || [];
        return skipLogicEngine.getVisibleSections();
    }, [skipLogicEngine, formData]);

    // Validation function for required fields
    const validateField = (field, value) => {
        if (field.required) {
            if (field.type === 'checkbox') {
                return value === true;
            } else if (field.type === 'file') {
                return value && (value instanceof File || (Array.isArray(value) && value.length > 0));
            } else if (field.type === 'programme-selector') {
                return Array.isArray(value) && value.length > 0;
            } else {
                return value && value.toString().trim().length > 0;
            }
        }
        return true;
    };

    // Validate entire section
    const validateSection = (sectionIndex) => {
        const section = formData.sections[sectionIndex];
        if (!section || !section.fields) return true;

        const errors = {};
        let isValid = true;

        section.fields
            .filter((field) => !skipLogicEngine || skipLogicEngine.shouldShowField(field.id))
            .forEach((field) => {
                const value = formValues[field.id];
                if (!validateField(field, value)) {
                    errors[field.id] = `${field.label} is required`;
                    isValid = false;
                }
            });

        setValidationErrors(prev => ({
            ...prev,
            ...errors
        }));

        setSectionValidation(prev => ({
            ...prev,
            [sectionIndex]: isValid
        }));

        return isValid;
    };

    // Enhanced navigation with validation
    const handleNext = () => {
        // Validate current section before proceeding
        if (!validateSection(activeSectionIndex)) {
            return;
        }

        // Clear validation errors for current section
        const section = formData.sections[activeSectionIndex];
        if (section && section.fields) {
            const clearedErrors = { ...validationErrors };
            section.fields.forEach(field => {
                delete clearedErrors[field.id];
            });
            setValidationErrors(clearedErrors);
        }

        if (!skipLogicEngine) {
            if (activeSectionIndex < formData.sections.length - 1) {
                setActiveSectionIndex(activeSectionIndex + 1);
            }
            return;
        }

        const nextIndex = skipLogicEngine.getNextAvailableSectionIndex(activeSectionIndex);
        if (nextIndex !== -1) {
            setActiveSectionIndex(nextIndex);
        }
    };

    // Enhanced back navigation with user role consideration
    const handleBack = () => {
        if (!skipLogicEngine) {
            if (activeSectionIndex > 0) {
                setActiveSectionIndex(activeSectionIndex - 1);
            } else {
                handleFormExit();
            }
            return;
        }

        const prevIndex = skipLogicEngine.getPreviousAvailableSectionIndex(activeSectionIndex);
        if (prevIndex !== -1) {
            setActiveSectionIndex(prevIndex);
        } else {
            handleFormExit();
        }
    };

    // Handle form exit based on user role
    const handleFormExit = () => {
        if (user?.userType === 'APPLICANT') {
            navigate('/applicant/dashboard');
        } else if (user?.roles?.includes('ROLE_ADMIN') || user?.roles?.includes('ROLE_STAFF')) {
            if (onCancel) {
                onCancel();
            } else {
                navigate('/dashboard');
            }
        } else {
            // Fallback for unknown user types
            navigate('/');
        }
    };    const handleInputChange = (fieldId, value) => {
        console.log('ApplicationForm - handleInputChange called for field:', fieldId, 'with value:', value);
        console.log('ApplicationForm - Value type:', typeof value, 'Is Array:', Array.isArray(value));
        setFormValues(prev => {
            const newValues = {
                ...prev,
                [fieldId]: value
            };
            console.log('ApplicationForm - New form values for field', fieldId, ':', newValues[fieldId]);
            return newValues;
        });
    };

    const handleFileUpload = async (field, files) => {
        const fieldId = field.id;
        setFileErrors(prev => ({ ...prev, [fieldId]: null }));
        
        if (!files || files.length === 0) {
            handleInputChange(fieldId, null);
            return;
        }

        const fileArray = Array.from(files);
        const maxFiles = field.allowMultiple ? 10 : 1;
        
        if (fileArray.length > maxFiles) {
            setFileErrors(prev => ({ 
                ...prev, 
                [fieldId]: `Maximum ${maxFiles} file(s) allowed` 
            }));
            return;
        }

        const processedFiles = [];
        
        for (let i = 0; i < fileArray.length; i++) {
            const file = fileArray[i];
            const maxSizeMB = field.maxFileSize || 5;
            const maxSizeBytes = maxSizeMB * 1024 * 1024;

            // Validate file type
            // if (!ImageCompressor.validateFileType(file, field.allowedFileTypes || 'all')) {
            //     setFileErrors(prev => ({ 
            //         ...prev, 
            //         [fieldId]: `File type not allowed. Expected: ${field.allowedFileTypes || 'all'}` 
            //     }));
            //     return;
            // }
            if (!ImageCompressor.validateFileType(file, field.allowedFileTypes || 'all')) {
                const errorMsg = `File type not allowed. Expected: ${field.allowedFileTypes || 'all'}`;
                setFileErrors(prev => ({ ...prev, [fieldId]: errorMsg }));
                showToast('Invalid File Type', errorMsg, 'error');
                return;
            }

            // Check file size before compression
            // if (file.size > maxSizeBytes && ImageCompressor.getFileTypeCategory(file) !== 'images') {
            //     setFileErrors(prev => ({ 
            //         ...prev, 
            //         [fieldId]: `File size exceeds ${maxSizeMB}MB limit` 
            //     }));
            //     return;
            // }
            if (file.size > maxSizeBytes && ImageCompressor.getFileTypeCategory(file) !== 'images') {
                const errorMsg = `File size exceeds ${maxSizeMB}MB limit`;
                setFileErrors(prev => ({ ...prev, [fieldId]: errorMsg }));
                showToast('File Too Large', errorMsg, 'error');
                return;
            }

            try {
                setUploadProgress(prev => ({ ...prev, [fieldId]: 30 }));

                let processedFile = file;
                
                // Compress images
                if (ImageCompressor.getFileTypeCategory(file) === 'images') {
                    setUploadProgress(prev => ({ ...prev, [fieldId]: 50 }));
                    processedFile = await ImageCompressor.compressImage(file, {
                        maxSizeKB: maxSizeMB * 1024 * 0.8, // 80% of max size
                        quality: 0.8
                    });
                    setUploadProgress(prev => ({ ...prev, [fieldId]: 80 }));
                }

                processedFiles.push(processedFile);
                setUploadProgress(prev => ({ ...prev, [fieldId]: 100 }));
                
            } 
            // catch (error) {
            //     console.error('File processing error:', error);
            //     setFileErrors(prev => ({ 
            //         ...prev, 
            //         [fieldId]: `Failed to process file: ${error.message}` 
            //     }));
            //     setUploadProgress(prev => ({ ...prev, [fieldId]: 0 }));
            //     return;
            // }
            catch (error) {
            console.error('File processing error:', error);
            setFileErrors(prev => ({ 
                ...prev, 
                [fieldId]: `Failed to process file: ${error.message}` 
            }));
            setUploadProgress(prev => ({ ...prev, [fieldId]: 0 }));
            
            // Show toast notification
            showToast(
                'File Processing Error', 
                `Failed to process ${file.name}: ${error.message}`,
                'error'
            );
            return;
        }
        }

        // Store processed files
        const finalValue = field.allowMultiple ? processedFiles : processedFiles[0];
        handleInputChange(fieldId, finalValue);
        
        // Clear progress after a short delay
        setTimeout(() => {
            setUploadProgress(prev => ({ ...prev, [fieldId]: 0 }));
        }, 1500);
    };

    const removeFile = (fieldId, fileIndex = null) => {
        const currentValue = formValues[fieldId];
        if (!currentValue) return;

        if (Array.isArray(currentValue) && fileIndex !== null) {
            const newFiles = currentValue.filter((_, index) => index !== fileIndex);
            handleInputChange(fieldId, newFiles.length > 0 ? newFiles : null);
        } else {
            handleInputChange(fieldId, null);
        }
        setFileErrors(prev => ({ ...prev, [fieldId]: null }));
    };    const saveForm = async () => {
        if (!formData) {
            showToast('Warning','No form data available','warning')
            return;
        }

        // Validate all sections before submission
        let allSectionsValid = true;
        const allErrors = {};

        for (let i = 0; i < formData.sections.length; i++) {
            const section = formData.sections[i];
            if (!section || !section.fields) continue;

            section.fields
                .filter((field) => !skipLogicEngine || skipLogicEngine.shouldShowField(field.id))
                .forEach((field) => {
                    const value = formValues[field.id];
                    if (!validateField(field, value)) {
                        allErrors[field.id] = `${field.label} is required`;
                        allSectionsValid = false;
                    }
                });
        }

        if (!allSectionsValid) {
            setValidationErrors(allErrors);
            showToast('Validation Error', 'Please fill in all required fields before submitting', 'error');
            return;
        }

        // Clear any existing validation errors
        setValidationErrors({});

        setSubmitting(true);        try {
            // Note: Demo mode has been removed to enable actual backend submissions
            // All form submissions now go through the backend API endpoints// Create FormData object for mixed content
            const formDataToSend = new FormData();
            const submissionData = {};
            const fileFields = [];

            // Process form values and separate files
            Object.entries(formValues).forEach(([fieldId, value]) => {
                if (value instanceof File) {
                    // Single file
                    formDataToSend.append('files', value);
                    fileFields.push(fieldId);
                    submissionData[fieldId] = value.name;
                } else if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
                    // Multiple files
                    value.forEach(file => {
                        formDataToSend.append('files', file);
                    });
                    fileFields.push(fieldId);
                    submissionData[fieldId] = value.map(f => f.name);
                } else if (value !== null && value !== undefined && value !== '') {
                    // Regular form data
                    submissionData[fieldId] = value;
                }            });            // For admin submissions, use the original structure
            formDataToSend.append('formName', formData.formName || formData.name);
            formDataToSend.append('formId', formData.id);
            formDataToSend.append('formConfig', JSON.stringify(formData));
            formDataToSend.append('formType', formData.type);
            
            // Get the actual user information for submittedBy
            const submittedBy = user?.email || user?.username || user?.name || 'Unknown User';
            formDataToSend.append('submittedBy', submittedBy);
            formDataToSend.append('formSubmissionJson', JSON.stringify(submissionData));


    
            // Append files to FormData
            // Object.entries(formValues).forEach(([fieldId, value]) => {
            //     const field = formData.sections
            //         .flatMap(section => section.fields)
            //         .find(f => f.id === fieldId);
                
            //     if (field?.type === 'file' && value instanceof File) {
            //         formDataToSend.append('files', value);
            //         formDataToSend.append('fileFields', fieldId); // To track which field the file belongs to
            //     }
            // });
    
            // Log the data being sent (for debugging)
            // console.log("Form submission payload:", {
            //     formData: submissionData,
            //     files: Object.entries(formValues)
            //         .filter(([_, value]) => value instanceof File)
            //         .map(([fieldId]) => fieldId)
            // });
    
            formDataToSend.forEach((value, key) => {
                console.log(key, value);
            });            // Send to backend
            let response;
            let apiUrl;

            // For admin submissions, use the type-specific endpoints
            if (formData.type === "undergraduate") {
                apiUrl = APIAppUnderGraduateCreate;
            } else if (formData.type === "graduate") {
                apiUrl = APIAppGraduateCreate;
            } else if (formData.type === "shortcourse") {
                apiUrl = APIAppShortCourseCreate;
            } else {
                showToast('Error',"Unknown form type:", formData.type,'error')
                return;
            }

            let result;
            
            // For admin mode, use the existing authenticated request method
            result = await makeAuthenticatedRequest(apiUrl, {
                method: 'POST',
                body: formDataToSend
            });

            if (result.status === "success") {
                showToast('Success!','Form submitted successfully','success')
                // setSubmitting(false);
                // onCancel(); // Uncomment if you have navigation
            } else {
                throw new Error(result.message || "Submission failed");
            }
        } catch (error) {
            console.log("Submission error:", error);
            showToast('Error',error.message || "Failed to submit form",'error');
        }
    };

    const renderField = useCallback((field) => {
        switch (field.type) {            case 'text':
            case 'email':
            case 'number':
                return (                    <TextField
                        fullWidth
                        label={field.label}
                        type={field.type}
                        value={formValues[field.id] || ''}
                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                        margin="normal"
                        required={field.required}
                        error={!!validationErrors[field.id]}
                        helperText={validationErrors[field.id]}
                    />
                );

            case 'checkbox':
                if (field.groupName) {
                    return (
                        <Button
                            startIcon={
                                <Box
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        border: '2px solid',
                                        borderColor: formValues[field.id] ? 'primary.main' : 'grey.500',
                                        bgcolor: formValues[field.id] ? 'primary.main' : 'transparent',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {formValues[field.id] && <CheckCircleOutline sx={{ color: 'white' }} />}
                                </Box>
                            }
                            onClick={() => {
                                if (!formValues[field.id]) {
                                    const newValues = { ...formValues };
                                    // Uncheck all other checkboxes in the same group
                                    formData.sections.forEach(section => {
                                        section.fields.forEach(f => {
                                            if (f.type === 'checkbox' && 
                                                f.groupName === field.groupName && 
                                                f.id !== field.id) {
                                                newValues[f.id] = false;
                                            }
                                        });
                                    });
                                    // Check the current checkbox
                                    newValues[field.id] = true;
                                    setFormValues(newValues);
                                } else {
                                    // Allow unchecking (optional: remove this else block if you want to prevent unchecking)
                                    handleInputChange(field.id, false);
                                }
                            }}
                            sx={{
                                textTransform: 'none',
                                color: 'text.primary',
                                justifyContent: 'flex-start',
                            }}
                        >
                            {field.label}
                        </Button>
                    );
                }                // Regular checkbox button (no group)
                return (
                    <Box>
                        <Button
                            startIcon={
                                <Box
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        border: '2px solid',
                                        borderColor: formValues[field.id] ? 'primary.main' : 'grey.500',
                                        bgcolor: formValues[field.id] ? 'primary.main' : 'transparent',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {formValues[field.id] && <CheckCircleOutline sx={{ color: 'white' }} />}
                                </Box>
                            }
                            onClick={() => handleInputChange(field.id, !formValues[field.id])}
                            sx={{
                                textTransform: 'none',
                                color: 'text.primary',
                                justifyContent: 'flex-start',
                            }}
                        >
                            {field.label}
                        </Button>
                        {validationErrors[field.id] && (
                            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                {validationErrors[field.id]}
                            </Typography>
                        )}
                    </Box>
                );

            // case 'radio':
            //     console.log('ApplicationForm - Rendering radio:', field.id, 'value:', formValues[field.id], 'options:', field.options);
            //     return (
            //         <FormControl component="fieldset" margin="normal">
            //             <Typography variant="subtitle1">{field.label}</Typography>
            //             <RadioGroup
            //                 value={formValues[field.id] || ''}
            //                 onChange={(e) => handleInputChange(field.id, e.target.value)}
            //                 >
            //                 {field.options.map((option, index) => (
            //                     <FormControlLabel
            //                         key={index}
            //                         value={option}
            //                         control={<Radio />}
            //                         label={option}
            //                     />
            //                 ))}                        
            //                 </RadioGroup>
            //         </FormControl>
            //     );

            case 'radio':
                console.log('ApplicationForm - Rendering radio:', field.id, 'value:', formValues[field.id], 'options:', field.options);
                return (
                        <FormControl component="fieldset" margin="normal" fullWidth>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>{field.label}</Typography>
                            <RadioGroup
                                value={formValues[field.id] || ''}
                                onChange={(e) => handleInputChange(field.id, e.target.value)}
                                sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                            >
                                {field.options.map((option, index) => (
                            <Button
                                key={index}
                                onClick={() => handleInputChange(field.id, option)}
                                sx={{
                                    textTransform: 'none',
                                    justifyContent: 'flex-start', // Align content to the left
                                    borderColor: 'grey.300',
                                    backgroundColor: 'transparent',
                                    '&:hover': {
                                        backgroundColor: 'grey.100',
                                    },
                                    position: 'relative',
                                    overflow: 'hidden',
                                    pl: 3,
                                    py: 1.2
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5
                                    }}
                                >
                                    {/* Custom radio indicator */}
                                    <Box
                                        sx={{
                                            width: 16,
                                            height: 16,
                                            border: '2px solid grey',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        {formValues[field.id] === option && (
                                            <Box
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    bgcolor: 'primary.main',
                                                }}
                                            />
                                        )}
                                    </Box>

                                    {/* Option label */}
                                    <Typography variant="body2">{option}</Typography>
                                </Box>
                            </Button>
                        ))}                            </RadioGroup>
                            {validationErrors[field.id] && (
                                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                                    {validationErrors[field.id]}
                                </Typography>
                            )}
                        </FormControl>

                );case 'select':
                return (
                    <FormControl fullWidth margin="normal" error={!!validationErrors[field.id]}>
                        <InputLabel>{field.label}</InputLabel>
                        <Select
                            value={formValues[field.id] || ''}
                            label={field.label}
                            onChange={(e) => handleInputChange(field.id, e.target.value)}
                            required={field.required}
                        >
                            {field.options.map((option, index) => (
                                <MenuItem key={index} value={option}>{option}</MenuItem>
                            ))}                        </Select>
                        {validationErrors[field.id] && (
                            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                                {validationErrors[field.id]}
                            </Typography>
                        )}
                    </FormControl>
                );

            case 'label':
                return (                    
                <Typography variant="h6" gutterBottom>
                        {field.label}
                    </Typography>
                );            case 'textarea':
                return (
                    <TextField
                        fullWidth
                        label={field.label}
                        multiline
                        rows={4}
                        value={formValues[field.id] || ''}
                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                        margin="normal"
                        required={field.required}                        variant="outlined"
                        error={!!validationErrors[field.id]}
                        helperText={validationErrors[field.id]}
                    />
                );            case 'date':
                return (
                    <TextField
                        fullWidth
                        label={field.label || "Date"}
                        type="date"
                        value={formValues[field.id] || ''}
                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                        margin="normal"
                        required={field.required}
                        variant="outlined"
                        error={!!validationErrors[field.id]}
                        helperText={validationErrors[field.id]}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        slotProps={{
                            input: {
                                shrink: true,
                            },                        }}
                    />
                );            case 'programme-selector':
                return (
                    <ProgrammeField
                        key={field.id}
                        field={field}
                        value={formValues[field.id] || []}
                        onChange={(selectedProgrammes) => handleInputChange(field.id, selectedProgrammes)}
                        error={validationErrors[field.id]}
                    />
                );

            case 'file':
                const currentFiles = formValues[field.id];
                const hasFiles = currentFiles && (Array.isArray(currentFiles) ? currentFiles.length > 0 : true);
                const progress = uploadProgress[field.id] || 0;
                const error = fileErrors[field.id];

                return (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                            {field.label}
                            {field.required && <span style={{ color: 'red' }}> *</span>}
                        </Typography>
                        
                        {/* File Type Info */}
                        {field.allowedFileTypes && field.allowedFileTypes !== 'all' && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Allowed: {field.allowedFileTypes === 'images' ? 'Images (JPG, PNG, GIF, WEBP)' : 
                                         field.allowedFileTypes === 'pdf' ? 'PDF files' : 
                                         field.allowedFileTypes === 'documents' ? 'Documents (PDF, DOC, DOCX)' : 
                                         field.allowedFileTypes}
                                {field.maxFileSize && ` | Max size: ${field.maxFileSize}MB`}
                            </Typography>
                        )}

                        {/* File Upload Button */}
                        <Button
                            variant="outlined"
                            component="label"
                            fullWidth
                            startIcon={<AttachFile />}
                            disabled={progress > 0}
                            sx={{ mb: 1 }}
                        >
                            {progress > 0 ? `Processing... ${progress}%` : 
                             hasFiles ? 'Change File(s)' : 'Choose File(s)'}
                            <input
                                type="file"
                                hidden
                                multiple={field.allowMultiple}
                                accept={field.allowedFileTypes === 'images' ? 'image/*' :
                                       field.allowedFileTypes === 'pdf' ? '.pdf' :
                                       field.allowedFileTypes === 'documents' ? '.pdf,.doc,.docx' : '*/*'}
                                onChange={(e) => handleFileUpload(field, e.target.files)}
                            />
                        </Button>

                        {/* Progress Bar */}
                        {progress > 0 && (
                            <LinearProgress 
                                variant="determinate" 
                                value={progress} 
                                sx={{ mb: 1 }}
                            />
                        )}                        {/* Error Display */}
                        {error && (
                            <Alert severity="error" sx={{ mb: 1 }}>
                                {error}
                            </Alert>
                        )}

                        {/* Validation Error Display */}
                        {validationErrors[field.id] && (
                            <Alert severity="error" sx={{ mb: 1 }}>
                                {validationErrors[field.id]}
                            </Alert>
                        )}

                        {/* Selected Files Display */}
                        {hasFiles && (
                            <Box sx={{ mt: 1 }}>
                                {Array.isArray(currentFiles) ? (
                                    currentFiles.map((file, index) => (
                                        <Chip
                                            key={index}
                                            label={`${file.name} (${ImageCompressor.formatFileSize(file.size)})`}
                                            onDelete={() => removeFile(field.id, index)}
                                            deleteIcon={<Delete />}
                                            variant="outlined"
                                            sx={{ mr: 1, mb: 1 }}
                                        />
                                    ))
                                ) : (
                                    <Chip
                                        label={`${currentFiles.name} (${ImageCompressor.formatFileSize(currentFiles.size)})`}
                                        onDelete={() => removeFile(field.id)}
                                        deleteIcon={<Delete />}
                                        variant="outlined"
                                        sx={{ mr: 1, mb: 1 }}
                                    />
                                )}
                            </Box>
                        )}
                    </Box>                );
            default:
                return null;
        }
    }, [formValues, handleInputChange, handleFileUpload, removeFile, uploadProgress, fileErrors]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        showToast('Error',error,'error');
        // return (
        //     <Box display="flex" justifyContent="center" mt={4}>
        //         <Typography color="error">{error}</Typography>
        //     </Box>
        // );
    }

    if (!formData) {
        showToast('Info','No form data available','info')
    }

    return (
        <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Button 
                    variant="contained" 
                    color='error'
                    startIcon={<ArrowBackIosIcon />}
                    onClick={onCancel}
                >
                    Back To Applications
                </Button>
                    <Typography variant="5" gutterBottom align="center">
                        {formData.name}
                    </Typography>
                <Button 
                    variant="contained" 
                    color='error'
                    startIcon={<CloseIcon />}
                    onClick={onCancel}
                >
                    Cancel
                </Button>
            </Box>
        <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>            
            <MobileStepper
                variant="text"
                steps={visibleSections.length}
                position="static"
                activeStep={visibleSections.findIndex(section => section.id === formData.sections[activeSectionIndex]?.id)}
                sx={{ 
                    maxWidth: 800,
                    mx: 'auto',
                    mb: 2,
                    flexGrow: 1,
                    bgcolor: 'transparent',
                    '& .MuiMobileStepper-dots': {
                        display: 'none' // Hide default dots
                    }
                }}
                nextButton={
                    <Button
                        size="small"
                        variant='contained'
                        color='secondary'
                        onClick={handleNext}
                        disabled={!skipLogicEngine || skipLogicEngine.getNextAvailableSectionIndex(activeSectionIndex) === -1}
                    >
                        Next
                        <KeyboardArrowDown />
                    </Button>
                }
                backButton={
                    <Button
                        size="small"
                        variant='contained'
                        color='secondary'
                        onClick={handleBack}
                        disabled={!skipLogicEngine || skipLogicEngine.getPreviousAvailableSectionIndex(activeSectionIndex) === -1}
                    >
                        <KeyboardArrowUp />
                        Back
                    </Button>
                }
            />
            
            {/* Custom step indicators with connecting lines */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mb: 2
            }}>
                {visibleSections.map((section, index) => {
                    const isActive = index === activeSectionIndex;
                    const isCompleted = index < activeSectionIndex;
                    
                    return (
                        <React.Fragment key={section.id}>
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    bgcolor: isActive ? 'primary.main' : 
                                            isCompleted ? 'primary.light' : 'grey.300',
                                    color: isActive ? 'common.white' : 
                                        isCompleted ? 'common.white' : 'text.primary',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold',
                                    position: 'relative'
                                }}
                            >
                                {index + 1}
                            </Box>
                            
                            {/* Connecting line (except after last step) */}
                            {index < visibleSections.length - 1 && (
                                <Box 
                                    sx={{
                                        height: 2,
                                        width: 40,
                                        bgcolor: isCompleted ? 'primary.light' : 'grey.300'
                                    }}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </Box>
            
            <AnimatePresence mode="wait">
                <motion.div
                    key={activeSectionIndex}
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -50, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <Card sx={{ mb: 2 }}>
                        <CardContent>                            
                            <Typography variant="h5" gutterBottom>
                                {formData.sections[activeSectionIndex].name}
                            </Typography>
                            
                            {formData.sections[activeSectionIndex].fields
                                .filter((field) => !skipLogicEngine || skipLogicEngine.shouldShowField(field.id))
                                .map((field) => (
                                    <Box key={field.id} sx={{ mb: 2 }}>
                                        {renderField(field)}
                                    </Box>
                                ))}
                        </CardContent>
                    </Card>
                </motion.div>
            </AnimatePresence>            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button
                    variant="contained"
                    onClick={handleBack}
                    color='secondary'
                    disabled={!skipLogicEngine || skipLogicEngine.getPreviousAvailableSectionIndex(activeSectionIndex) === -1}
                    startIcon={<KeyboardArrowUp />}
                >
                    {(!skipLogicEngine || skipLogicEngine.getPreviousAvailableSectionIndex(activeSectionIndex) === -1) ? 'Back to Dashboard' : 'Previous'}
                </Button>
                <Button
                    variant="contained"
                    color='primary'
                    onClick={handleNext}
                    disabled={!skipLogicEngine || skipLogicEngine.getNextAvailableSectionIndex(activeSectionIndex) === -1}
                    endIcon={<KeyboardArrowDown />}
                >
                    {(!skipLogicEngine || skipLogicEngine.getNextAvailableSectionIndex(activeSectionIndex) === -1) ? 'Review & Submit' : 'Next Section'}
                </Button>
            </Box>
            
            {(!skipLogicEngine || skipLogicEngine.getNextAvailableSectionIndex(activeSectionIndex) === -1) && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        size="large"
                        onClick={saveForm}
                        disabled={submitting}
                    >
                        {submitting ? <CircularProgress size={24} /> : "Submit Application"}
                    </Button>
                </Box>
            )}
        </Box>
        </>
    );
}

export default ApplicationForm;
